import { BaseGameScene } from './BaseGameScene';
import { HEADER_HEIGHT } from '../config';
import { COLORS, DISPLAY_FONT } from '../ui/theme';
import { store } from '../state/store';
import { Button } from '../ui/Button';
import { qualityGrade } from '../systems/MixingSystem';
import { customersSystem } from '../systems/CustomerSystem';
import { baking } from '../systems/BakingSystem';
import { ovenTimers } from '../systems/OvenTimers';

const GRADE_DISPLAY: Record<string, { label: string; emoji: string; color: string }> = {
  perfect: { label: 'PERFECT', emoji: '\u{2728}', color: '#1f8b3a' },
  good:    { label: 'GOOD',    emoji: '\u{1F60A}', color: '#4a9a2f' },
  okay:    { label: 'OKAY',    emoji: '\u{1F610}', color: '#d9a520' },
  bad:     { label: 'BAD',     emoji: '\u{1F525}', color: '#cc4444' },
};

export class DayEndScene extends BaseGameScene {
  constructor() {
    super({ key: 'DayEnd' });
  }

  // DayEnd is a transient scene; suppress nav highlight by claiming Counter.
  protected sceneName(): Extract<'Menu' | 'Kitchen' | 'Oven' | 'Counter', string> { return 'Counter'; }
  protected title(): string { return ''; }
  protected backgroundColor(): number { return COLORS.bgDayEnd; }

  create(): void {
    this.setupChrome();
    const w = this.scale.width;
    const h = this.scale.height;

    this.add.text(w / 2, HEADER_HEIGHT + 52, `End of Day ${store.state.day}`, {
      fontFamily: DISPLAY_FONT, fontSize: '52px', fontStyle: '700', color: '#553089',
    }).setOrigin(0.5);

    // Revenue banner
    const revenue = store.state.todaysServes.reduce((s, e) => s + e.coinsEarned, 0);
    const served = store.state.todaysServes.filter((e) => e.served).length;
    this.add.text(w / 2, HEADER_HEIGHT + 112,
      `\u{1FA99} ${revenue} coins earned  •  ${served} customer${served === 1 ? '' : 's'} served`, {
        fontFamily: '"VT323", monospace', fontSize: '34px', color: '#c2871f',
      }).setOrigin(0.5);

    const panelY = HEADER_HEIGHT + 160;
    const panelH = h - panelY - 130;
    const colW = 560;
    const gap = 40;
    const leftX = w / 2 - colW - gap / 2;
    const rightX = w / 2 + gap / 2;

    // ── Left: what you baked ────────────────────────────────────────────────
    this.panel(leftX, panelY, colW, panelH);
    this.add.text(leftX + colW / 2, panelY + 26, 'YOU BAKED', {
      fontFamily: DISPLAY_FONT, fontSize: '22px', fontStyle: '700', color: '#553089',
    }).setOrigin(0.5);

    const batches = store.state.todaysMenu;
    if (batches.length === 0) {
      this.add.text(leftX + colW / 2, panelY + panelH / 2, '(nothing — try again tomorrow!)', {
        fontFamily: DISPLAY_FONT, fontSize: '20px', fontStyle: 'italic', color: '#888888',
      }).setOrigin(0.5);
    } else {
      batches.slice(0, 8).forEach((batch, i) => {
        const recipe = store.recipeById(batch.recipe);
        const y = panelY + 62 + i * 52;
        const grade = batch.status === 'ready' ? qualityGrade(batch.mixAccuracy, batch.doneness) : null;
        const gd = grade ? GRADE_DISPLAY[grade] : { label: 'WASTED', emoji: '\u{1F5D1}', color: '#888888' };

        this.add.text(leftX + 26, y, `${gd.emoji}  ${recipe?.name ?? batch.recipe}`, {
          fontFamily: DISPLAY_FONT, fontSize: '21px', fontStyle: '500', color: '#2a1f4a',
        });
        this.add.text(leftX + colW - 120, y + 3, grade ? `${(batch.mixAccuracy * 100).toFixed(0)}% mix` : batch.status, {
          fontFamily: DISPLAY_FONT, fontSize: '14px', color: '#55488a',
        }).setOrigin(1, 0);
        this.add.text(leftX + colW - 26, y, gd.label, {
          fontFamily: '"VT323", monospace', fontSize: '24px', color: gd.color,
        }).setOrigin(1, 0);
      });
    }

    // ── Right: customer reactions ───────────────────────────────────────────
    this.panel(rightX, panelY, colW, panelH);
    this.add.text(rightX + colW / 2, panelY + 26, 'CUSTOMERS', {
      fontFamily: DISPLAY_FONT, fontSize: '22px', fontStyle: '700', color: '#553089',
    }).setOrigin(0.5);

    const serves = store.state.todaysServes;
    if (serves.length === 0) {
      this.add.text(rightX + colW / 2, panelY + panelH / 2, '(nobody was served today)', {
        fontFamily: DISPLAY_FONT, fontSize: '20px', fontStyle: 'italic', color: '#888888',
      }).setOrigin(0.5);
    } else {
      serves.slice(0, 8).forEach((entry, i) => {
        const customer = store.customerById(entry.customerId);
        const recipe = entry.recipe ? store.recipeById(entry.recipe) : undefined;
        const r = customersSystem.reactionFor(entry);
        const y = panelY + 62 + i * 52;

        this.add.text(rightX + 26, y, `${r.emoji}  ${customer?.name ?? entry.customerId}`, {
          fontFamily: DISPLAY_FONT, fontSize: '21px', fontStyle: '500', color: '#2a1f4a',
        });
        if (recipe) {
          this.add.text(rightX + colW - 140, y + 3, recipe.name, {
            fontFamily: DISPLAY_FONT, fontSize: '14px', color: '#55488a',
          }).setOrigin(1, 0);
        }
        this.add.text(rightX + colW - 26, y, r.text, {
          fontFamily: DISPLAY_FONT, fontSize: '17px', fontStyle: '600', color: r.color,
        }).setOrigin(1, 0);
      });
      if (serves.length > 8) {
        this.add.text(rightX + colW / 2, panelY + panelH - 24, `…and ${serves.length - 8} more`, {
          fontFamily: DISPLAY_FONT, fontSize: '15px', fontStyle: 'italic', color: '#888888',
        }).setOrigin(0.5);
      }
    }

    new Button(this, w / 2, h - 70, 'Next Day →', {
      fill: COLORS.accentGreen, hoverFill: 0x5fe28b, fontSize: '28px', width: 280, height: 62,
      onClick: () => {
        // Release runtime leftovers before the state resets.
        for (const bake of store.state.activeBakes) baking.forget(bake.id);
        ovenTimers.clear(0);
        ovenTimers.clear(1);
        store.finishDay();
        this.scene.start('Kitchen');
      },
    });
  }
}
